import { type Checkpoint, isCheckpoint } from './checkpoints';
import { type Profile, isProfile } from './profiles';
import { ROLE_LABELS, type Role, isRole } from './roles';

export const REVIEW_BODY_MARKER = '<!-- asdp-review v1 -->';

export const DECISIONS = [
  'Approved',
  'Approved with conditions',
  'Concurs',
  'Changes requested',
  'Comment',
] as const;
export type Decision = (typeof DECISIONS)[number];

export type ReviewedDocument = { path: string; sha: string };

export type ReviewBody = {
  checkpoint: Checkpoint;
  profile: Profile;
  role: Role;
  decision: Decision;
  conditions?: string;
  reviewed: ReviewedDocument[];
  /** Free text shown to people, below the record. */
  comment?: string;
};

const roleByLabel = new Map(
  Object.entries(ROLE_LABELS).map(([role, label]) => [label, role as Role]),
);

/** The structured review body (gate-review spec "Reading and deciding"). */
export function buildReviewBody(review: ReviewBody): string {
  const lines = [
    REVIEW_BODY_MARKER,
    `Checkpoint: ${review.checkpoint} · Profile: ${review.profile} · Role: ${ROLE_LABELS[review.role]}`,
    `Decision: ${review.decision}`,
  ];
  const conditions = review.conditions?.trim();
  if (conditions) lines.push(`Conditions: ${conditions.replaceAll(/\s*\n\s*/g, ' ')}`);
  if (review.reviewed.length > 0) {
    lines.push(
      `Reviewed: ${review.reviewed.map(doc => `${doc.path}@${doc.sha}`).join(', ')}`,
    );
  }
  const comment = review.comment?.trim();
  return comment ? `${lines.join('\n')}\n\n${comment}` : lines.join('\n');
}

const field = (body: string, name: string) =>
  new RegExp(`^${name}: (.*)$`, 'm').exec(body)?.[1]?.trim();

/** The record in a review body, or undefined when it has none or is malformed. */
export function parseReviewBody(body: string | null | undefined): ReviewBody | undefined {
  if (!body?.startsWith(REVIEW_BODY_MARKER)) return undefined;
  const header = /^Checkpoint: (\S+) · Profile: (\S+) · Role: (.+)$/m.exec(body);
  const decision = field(body, 'Decision');
  if (!header || !decision) return undefined;
  const [, checkpoint, profile, roleLabel] = header;
  const role = roleByLabel.get(roleLabel.trim());
  if (
    !isCheckpoint(checkpoint) ||
    !isProfile(profile) ||
    !isRole(role) ||
    !(DECISIONS as readonly string[]).includes(decision)
  ) {
    return undefined;
  }
  const reviewed = (field(body, 'Reviewed') ?? '')
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean)
    .map(entry => {
      const at = entry.lastIndexOf('@');
      return { path: entry.slice(0, at), sha: entry.slice(at + 1) };
    })
    .filter(doc => doc.path && doc.sha);
  const separator = body.indexOf('\n\n');
  const comment = separator >= 0 ? body.slice(separator + 2).trim() : '';
  return {
    checkpoint,
    profile,
    role,
    decision: decision as Decision,
    reviewed,
    ...(field(body, 'Conditions') && { conditions: field(body, 'Conditions') }),
    ...(comment && { comment }),
  };
}
